import { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";

import { BaseModal, ModalContent } from "../giselle-modal";
import {
  ProgressBar,
  ProgressBarPercentage,
} from "../progress-bar/progress-bar";
import CreativeEditorSDK, { Configuration } from "@cesdk/cesdk-js";
import { Button } from "@/components/ui/button";
import { addUploadedAsset } from "../../utils/cesdk-upload-asset-source";
import { ConfirmModal } from "../confirm-required/confirm-modal";

// Supported MIME types for upload in CESDK editor
// NOTE: These MIME types apply to ALL upload buttons globally.
// CESDK does not support per-section MIME filtering in the file picker.
// Runtime validation in handleCESDKUpload() enforces per-section rules and shows clear error messages.
const CESDK_SUPPORTED_MIME_TYPES = [
  // Images
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/bmp",
  "image/tiff",
  // Videos
  "video/mp4",
  "video/quicktime",
  "video/x-msvideo",
  "video/webm",
  // PDFs
  "application/pdf",
  // Audio
  "audio/mpeg", // .mp3
  "audio/wav", // .wav
  "audio/x-wav", // .wav (alternative)
  "audio/aac", // .aac
  "audio/mp4", // .m4a
  "audio/x-m4a", // .m4a (alternative)
];

export function CreativeBaseEditorModal({
  onClose,
  progress,
  onInit,
  onSave,
  title,
  style,
  saveButtonText = "Save",
  message,
  isSaving = false,
  saveDisabled = false,
  onUploadHandler,
  hasChanges = false,
}: {
  onClose(): void;
  progress?: {
    label: string;
    percentage?: number;
  };
  onInit: (instance: CreativeEditorSDK) => Promise<void>;
  onSave(instance: CreativeEditorSDK): Promise<void>;
  title: string;
  style?: React.CSSProperties;
  saveButtonText?: string;
  message?: string;
  isSaving?: boolean;
  saveDisabled?: boolean;
  onUploadHandler?: (
    file: File,
    onProgress: (progress: number) => void,
    context?: { sourceId?: string; group?: string }
  ) => Promise<{ id: string; [key: string]: unknown }>;
  hasChanges?: boolean;
}) {
  const cesdk_container = useRef(null);
  const cesdkRef = useRef<CreativeEditorSDK>();
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [showConfirmClose, setShowConfirmClose] = useState(false);

  // Handle close with confirmation if there are unsaved changes
  const handleClose = useCallback(() => {
    if (hasChanges) {
      setShowConfirmClose(true);
    } else {
      onClose();
    }
  }, [hasChanges, onClose]);

  // Handle ESC key to show confirmation modal instead of closing directly
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        handleClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleClose]);

  const handleConfirmClose = () => {
    setShowConfirmClose(false);
    onClose();
  };

  const handleCancelClose = () => {
    setShowConfirmClose(false);
  };

  // Wrap upload handler to refresh asset sources after upload completes
  const wrappedUploadHandler = onUploadHandler
    ? async (
        file: File,
        onProgress: (progress: number) => void,
        context?: { sourceId?: string; group?: string }
      ) => {
        const result = await onUploadHandler(file, onProgress, context);

        // Add the uploaded asset to in-memory cache so findAssets returns it
        if (result && result.id) {
          addUploadedAsset(result);
        }

        // Notify CESDK that asset source contents have changed to refresh the UI
        // Small delay to let CESDK's internal addAsset call complete first
        if (cesdkRef.current) {
          const cesdk = cesdkRef.current;
          setTimeout(() => {
            try {
              const assetApi = cesdk.engine.asset;
              assetApi.assetSourceContentsChanged("sked-uploads-video");
              assetApi.assetSourceContentsChanged("sked-uploads-image");
              assetApi.assetSourceContentsChanged("sked-uploads-audio");
            } catch {
              // Silently ignore - asset sources may not be registered
            }
          }, 100);
        }
        return result;
      }
    : undefined;

  const config: Configuration = {
    license: "kvHPtn9Qj8lIKrTGvfHZfbeWYjNNMftyloOPYVOgH_EkqNwiWbJ1kbrQE8BHVMYH",
    ui: {
      elements: {
        blocks: {
          "//ly.img.ubq/page": {
            manage: false,
          },
        },
        panels: {
          inspector: {
            show: true,
          },
        },
        libraries: {
          replace: {
            floating: true,
            autoClose: true,
          },
          insert: {
            autoClose: false,
            floating: false,
          },
        },
        navigation: {
          title: title,
        },
      },
    },
    callbacks: {
      ...(wrappedUploadHandler && {
        onUpload: {
          callback: wrappedUploadHandler,
          supportedMimeTypes: CESDK_SUPPORTED_MIME_TYPES,
        },
      }),
    },
  };

  useEffect(() => {
    if (!cesdk_container.current) {
      return;
    }

    let cleanedUp = false;
    let instance: CreativeEditorSDK;
    function cleanup() {
      cleanedUp = true;
      if (instance) {
        instance.dispose();
      }
      cesdkRef.current = undefined;
    }
    CreativeEditorSDK.create(cesdk_container.current, config)
      .then(async (_instance) => {
        instance = _instance;
        if (cleanedUp) {
          instance.dispose();
          return;
        }

        cesdkRef.current = instance;
        await onInit(instance);

        setInitialized(true);
      })
      .catch(async (error) => {
        if (cleanedUp) {
          return;
        }
        await instance?.createDesignScene();
        console.error("Error initializing CreativeEditorSDK:", error);
        setError(
          `Failed to initialize editor or image. (Error: ${error.message})`
        );
        setInitialized(false);
      });
    return () => {
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Modal
        show
        as={BaseModal}
        onHide={handleClose}
        onShow={() => {}}
        backdrop={false}
        keyboard={false}
      >
        <ModalContent>
          <div
            ref={cesdk_container}
            className="cesdkStyle"
            style={{
              width: "100%",
              height: "calc(100% - 80px)",
              ...style,
            }}
          />
          <ButtonContainer>
            <Button
              variant="blue-secondary"
              className="cancel-button"
              size="xs"
              onClick={handleClose}
            >
              Cancel
            </Button>
            {error && (
              <div
                className="error-message"
                style={{ color: "#FA4F38", fontSize: 12 }}
              >
                {error}
              </div>
            )}

            <div className="flex gap-2 items-center">
              {message && (
                <InfoMessage>
                  <i className="fa-regular fa-circle-info"></i>
                  {message}
                </InfoMessage>
              )}

              <Button
                variant="blue-primary"
                className="save-button"
                disabled={!initialized || isSaving || saveDisabled}
                style={{
                  color: initialized && !saveDisabled ? "blue" : "white",
                }}
                size="xs"
                onClick={async () => {
                  if (!cesdkRef.current) {
                    return;
                  }
                  onSave(cesdkRef.current);
                }}
              >
                {isSaving ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin"></i>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fa-regular fa-save"></i>
                    {saveButtonText}
                  </>
                )}
              </Button>
            </div>
          </ButtonContainer>
        </ModalContent>
        {progress && (
          <Cover>
            {progress.percentage === undefined ? (
              <ProgressBar>{progress.label}</ProgressBar>
            ) : (
              <ProgressBarPercentage
                $percentage={progress.percentage}
                style={{ width: "80%" }}
              >
                {progress.label}
              </ProgressBarPercentage>
            )}
          </Cover>
        )}
      </Modal>
      <ConfirmModal
        show={showConfirmClose}
        title="Discard changes?"
        message="You have unsaved changes. Are you sure you want to close the editor? Your changes will be lost."
        confirmText="Discard"
        cancelText="Keep editing"
        confirmType={{ alert: true }}
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
      />
    </>
  );
}

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 10px;
  > .save-button {
    margin-left: auto;
  }
`;

const Cover = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background: rgba(0, 0, 0, 0.5);

  display: flex;
  align-items: center;
  justify-content: center;

  ${ProgressBar} {
    width: 80%;
  }
`;

const Modal = styled.div`
  width: 95%;
  max-width: none;
  height: 90%;

  > .modal-content {
    width: 100%;
    height: 100%;
    > ${ModalContent} {
      height: 100%;
      margin: 16px;
      margin-bottom: 0;
      overflow: hidden;
    }
  }
`;

const InfoMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 670px;
  border-radius: 8px;
  background: var(--Primary-P50, #edeaff);
  display: inline-flex;
  padding: 10px 14px;
  align-items: center;
  gap: 8px;
  color: var(--Primary-P500, #4f31ff);
  font-family: var(--font-family-body, Inter);
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
`;
